import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { BookOpen, EyeOff, Flag, Lock, Pencil, Send, Undo2, Users, WifiOff, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  INTERVIEW_FLAG_KINDS,
  INTERVIEW_FLAG_LABELS,
  INTERVIEW_MODE_LABELS,
  submitProblems,
  type CockpitView,
  type InterviewFlagKind,
  type InterviewFlagView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { Chip } from '../../../components/Chip';
import { Textarea } from '../../../components/Input';
import { Kbd, MOD_KEY } from '../../../components/Kbd';
import { Popover, usePopover } from '../../../components/Popover';
import { Segmented } from '../../../components/Segmented';
import { SkeletonLines } from '../../../components/Skeleton';
import { INTERVIEW_STATUS_TONES } from '../../../components/status-tones';
import { ToneChip } from '../../../components/Tone';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { formatDateTime, formatTime } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { createUserPreferenceStore } from '../../../lib/preferences';
import { useNow } from '../../../lib/use-now';
import { useNetworkStatus } from '../../../shell/SocketProvider';
import { useCurrentSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { cockpitKey, scorecardsApi, useCockpit } from '../api/scorecards-api';
import { CandidateColumn } from '../components/CandidateColumn';
import { KitSheet } from '../components/KitSheet';
import { ScorecardForm } from '../components/ScorecardForm';
import { useScorecardDraft, type ScorecardDraftState } from '../hooks/use-scorecard-draft';
import { sameDraft } from '../hooks/draft-store';
import styles from './Cockpit.module.css';

const HINTS_KEY = 'cockpit.hints.dismissed';
const SECOND_MS = 1_000;
const MINUTE_MS = 60_000;
const SECONDS_PER_MINUTE = 60;

type Tab = 'score' | 'candidate';

/**
 * INT-01 — the interview cockpit. The screen open during the interview: the candidate on
 * the left, my scorecard on the right, the blind-mode notice and Submit at the foot (E9).
 * Where N-31, N-37 and N-38 land. Nothing typed here is lost to a dropped connection.
 */
export function CockpitPage({ interviewId }: { readonly interviewId: string }): ReactNode {
  const query = useCockpit(interviewId);
  const navigate = useNavigate();
  const userId = useCurrentSession()?.id ?? 'anonymous';

  if (query.isPending) return <SkeletonLines count={12} />;
  if (query.error) {
    if (query.error.status === 403 && query.error.details.isOverseer === true) {
      // A recruiter or HR Lead opening a panelist's link: the scorecards view is theirs.
      void navigate({ to: '/interviews/$interviewId/scorecards', params: { interviewId }, replace: true });
      return <SkeletonLines count={12} />;
    }
    if (query.error.status === 403) return <ForbiddenState resource="this scorecard" contact="The recruiter for this job" />;
    if (query.error.status === 404) return <NotFoundState onGoBack={() => void navigate({ to: '/interviews/mine' })} />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  return <Cockpit key={query.data.scorecard.id} view={query.data} userId={userId} />;
}

function Cockpit({ view, userId }: { readonly view: CockpitView; readonly userId: string }): ReactNode {
  const queryClient = useQueryClient();
  const toasts = useToasts();
  const network = useNetworkStatus();
  const draftState = useScorecardDraft(view.scorecard, userId);
  const { draft } = draftState;
  const [tab, setTab] = useState<Tab>('score');
  const [kitFor, setKitFor] = useState<{ readonly isOpen: boolean; readonly criterionId: string | null }>({ isOpen: false, criterionId: null });
  const hasKits = useModule('interview_kits');
  const [isAttempted, setIsAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const submitted = view.scorecard.submittedDraft;
  const hasChangesSinceSubmit = submitted !== null && !sameDraft(draft, submitted);
  const [isEditing, setIsEditing] = useState(hasChangesSinceSubmit);
  const formRef = useRef<HTMLDivElement | null>(null);

  const isObserver = view.scorecard.role === 'observer';
  const isSubmitted = view.scorecard.currentVersion > 0;
  const isReadOnly = !view.can.write || (isSubmitted && !isEditing);
  const problems = submitProblems(draft, isObserver);

  // F11.01 / F11.02 — counted once per visit, not on every refetch.
  useEffect(() => {
    void scorecardsApi.opened(view.scorecard.id).catch(() => undefined);
  }, [view.scorecard.id]);

  async function submit(): Promise<void> {
    setIsAttempted(true);
    if (problems.length > 0) {
      setTab('score');
      formRef.current?.querySelector('[aria-invalid="true"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    setIsSubmitting(true);
    try {
      const sent = await draftState.saveNow();
      if (!sent) {
        toasts.show({ tone: 'warning', message: network === 'online' ? 'Answer the question above first' : "You're offline", detail: network === 'online' ? undefined : 'Your scorecard is saved on this device. Submit when you are back online.' });
        return;
      }
      const next = await scorecardsApi.submit(view.scorecard.id, { baseRevision: sent.baseRevision, draft: sent.draft, isOfflineSync: false, isOverwrite: false, changeNote: isSubmitted && changeNote.trim() !== '' ? changeNote.trim() : null });
      queryClient.setQueryData(cockpitKey(view.interview.id), next);
      draftState.settle(next.scorecard);
      setIsEditing(false);
      setChangeNote('');
      setIsAttempted(false);
      toasts.show({ tone: 'success', message: isSubmitted ? `Version ${next.scorecard.currentVersion} submitted` : 'Scorecard submitted', detail: next.can.seePeers ? "The panel's marks for this round are open to you now." : undefined });
    } catch (error) {
      toasts.show({ tone: 'danger', message: "Couldn't submit", detail: `${errorMessage(error)} Your scorecard is kept.` });
      if (error instanceof ApiError && error.status === 409) void queryClient.invalidateQueries({ queryKey: cockpitKey(view.interview.id) });
    } finally {
      setIsSubmitting(false);
    }
  }

  function discard(): void {
    if (submitted) draftState.change(submitted);
    setIsEditing(false);
    setChangeNote('');
    setIsAttempted(false);
  }

  // 06-qol §2: K opens the kit; Ctrl/Cmd+Enter submits, as every form does.
  const submitRef = useRef(submit);
  useEffect(() => {
    submitRef.current = submit;
  });
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        if (!isReadOnly) void submitRef.current();
        return;
      }
      if (!hasKits || event.key.toLowerCase() !== 'k' || event.ctrlKey || event.metaKey || event.altKey || isTyping(event.target)) return;
      event.preventDefault();
      setKitFor((current) => ({ isOpen: !current.isOpen, criterionId: null }));
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isReadOnly, hasKits]);

  const interview = view.interview;
  return (
    <div className={styles.screen}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <Link to="/interviews/mine" className={styles.back}>
            ← My interviews
          </Link>
          <h1 className={styles.title}>{view.candidate.fullName}</h1>
          <p className={styles.meta}>
            {interview.jobTitle} · Round {interview.roundNumber} of {Math.max(interview.roundCount, interview.roundNumber)} · {interview.roundName} · {INTERVIEW_MODE_LABELS[interview.mode]}
          </p>
        </div>
        <Timer view={view} />
        <div className={styles.headerActions}>
          <SaveState state={draftState} />
          {hasKits && (
            <Button variant="outline" icon={BookOpen} onClick={() => setKitFor({ isOpen: true, criterionId: null })}>
              Kit <Kbd>K</Kbd>
            </Button>
          )}
          {view.can.flag && <FlagMenu interviewId={interview.id} flags={view.flags} />}
          {view.can.seePeers && (
            <Link to="/interviews/$interviewId/scorecards" params={{ interviewId: interview.id }} className={styles.linkButton}>
              <Users size={16} aria-hidden /> Panel's scorecards
            </Link>
          )}
        </div>
      </header>

      <HintBar userId={userId} hasKits={hasKits} />

      {isObserver && <p className={styles.notice}>You're observing. Write your notes; any scores you give are shown as an observer's and never count towards the decision.</p>}
      {draftState.isDeviceStorageFailing && (
        <p className={styles.danger} role="alert">
          This device can't keep a copy of your scorecard (storage full, or private browsing). It is still saved to the server while you're online.
        </p>
      )}
      {draftState.conflict && <ConflictBanner state={draftState} />}
      <StateBanner view={view} isEditing={isEditing} onEdit={() => setIsEditing(true)} />

      <div className={styles.tabs}>
        <Segmented<Tab>
          label="Show"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'score', label: 'Scorecard' },
            { value: 'candidate', label: 'Candidate' },
          ]}
        />
      </div>

      <div className={styles.regions} data-tab={tab}>
        <section className={`${styles.left} ${styles.scroll}`} aria-label="Candidate">
          <CandidateColumn view={view} />
        </section>
        <section ref={formRef} className={`${styles.right} ${styles.scroll}`} aria-label="My scorecard">
          <ScorecardForm
            draft={draft}
            onChange={draftState.change}
            isReadOnly={isReadOnly}
            isObserver={isObserver}
            isMarkMissing={isAttempted && !isObserver && draft.overallMark === null}
            isCommentMissing={isAttempted && draft.notes.trim() === ''}
            kitQuestionCount={view.kit?.questions.length ?? 0}
            guide={view.guide}
            {...(hasKits ? { onOpenKit: () => setKitFor({ isOpen: true, criterionId: null }) } : {})}
          />
        </section>
      </div>

      <footer className={styles.footer} aria-label="Submit">
        {/* #9 (ADR 0077): always blind within the round, enforced by the server. */}
        <p className={styles.blind}>
          {view.can.seePeers ? (
            <>You submitted, so the panel's marks for this round are open to you.</>
          ) : (
            <>
              <EyeOff size={16} aria-hidden /> The other panelists' marks and comments unlock after you submit yours.
            </>
          )}
        </p>
        {isEditing && isSubmitted && (
          <div className={styles.changeNote}>
            <Textarea aria-label="What changed (optional)" rows={1} value={changeNote} maxLength={1000} placeholder="What changed? (optional)" onChange={(event) => setChangeNote(event.target.value)} />
          </div>
        )}
        {isAttempted && problems.length > 0 && !isReadOnly && (
          <p className={styles.problems} role="alert">
            {problems.length === 1 ? problems[0] : `${problems.length} things to finish: ${problems[0]}`}
          </p>
        )}
        <span className={styles.spacer} />
        {!view.can.write && view.can.lockedReason && (
          <span className={styles.quiet}>
            <Lock size={14} aria-hidden /> {view.can.lockedReason}
          </span>
        )}
        {view.can.write && isSubmitted && !isEditing && (
          <Button variant="outline" icon={Pencil} onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        )}
        {view.can.write && isSubmitted && isEditing && (
          <Button variant="ghost" icon={Undo2} onClick={discard}>
            Discard changes
          </Button>
        )}
        {view.can.write && (!isSubmitted || isEditing) && (
          <SubmitButton view={view} network={network} isSubmitting={isSubmitting} isEditing={isSubmitted} isUnchanged={isSubmitted && !hasChangesSinceSubmit} onSubmit={() => void submit()} />
        )}
      </footer>

      {hasKits && <KitSheet kit={view.kit} isOpen={kitFor.isOpen} onClose={() => setKitFor({ isOpen: false, criterionId: null })} />}
    </div>
  );
}

function SubmitButton({ view, network, isSubmitting, isEditing, isUnchanged, onSubmit }: { readonly view: CockpitView; readonly network: string; readonly isSubmitting: boolean; readonly isEditing: boolean; readonly isUnchanged: boolean; readonly onSubmit: () => void }): ReactNode {
  const now = useNow(SECOND_MS * 15);
  const opensAt = view.can.submitFrom ? new Date(view.can.submitFrom).getTime() : null;
  const isBeforeStart = opensAt !== null && now < opensAt;
  const blocked = view.can.submitBlockedReason ?? (isBeforeStart && view.can.submitFrom ? `Submit opens when the interview starts, ${formatTime(view.can.submitFrom)}.` : null);
  const isOffline = network === 'offline';
  return (
    <>
      {(blocked || isOffline) && (
        <span className={styles.quiet}>
          {isOffline ? (
            <>
              <WifiOff size={14} aria-hidden /> Offline — submit when you're back. Your work is kept.
            </>
          ) : (
            blocked
          )}
        </span>
      )}
      <Button variant="solid" icon={Send} isPending={isSubmitting} disabled={blocked !== null || isOffline || isUnchanged} onClick={onSubmit} title={isUnchanged ? 'Nothing has changed yet' : undefined}>
        {isEditing ? 'Submit changes' : 'Submit'} <Kbd inverse>{MOD_KEY}</Kbd>
        <Kbd inverse>Enter</Kbd>
      </Button>
    </>
  );
}

/** The header's clock (E9 "duration timer"): counts down to the start, then up against the round's length. */
function Timer({ view }: { readonly view: CockpitView }): ReactNode {
  const now = useNow(SECOND_MS);
  const { startsAt, durationMinutes, status } = view.interview;
  if (status === 'completed' || status === 'panelist_no_show') return <ToneChip tone={INTERVIEW_STATUS_TONES[status]}>Interview over</ToneChip>;
  if (!startsAt) return <ToneChip tone={INTERVIEW_STATUS_TONES.unscheduled}>No time booked</ToneChip>;
  const start = new Date(startsAt).getTime();
  if (now < start) {
    const minutes = Math.ceil((start - now) / MINUTE_MS);
    return <ToneChip tone={INTERVIEW_STATUS_TONES[status]}>{minutes > 90 ? `Starts ${formatDateTime(startsAt)}` : `Starts in ${minutes} min`}</ToneChip>;
  }
  const elapsed = now - start;
  const planned = durationMinutes * MINUTE_MS;
  const isOver = elapsed > planned;
  return (
    <span className={`${styles.timer} ${isOver ? styles.timerOver : ''}`} role="timer" aria-live="off">
      {clock(elapsed)} <span className={styles.timerOf}>of {clock(planned)}</span>
    </span>
  );
}

function SaveState({ state }: { readonly state: ScorecardDraftState }): ReactNode {
  if (state.status === 'conflict') return <Chip tone="warning">Changed on another device</Chip>;
  if (state.status === 'waiting') {
    return (
      <Chip tone="warning">
        <WifiOff size={12} aria-hidden /> Offline — saved on this device
      </Chip>
    );
  }
  if (state.status === 'error') return <Chip tone="danger">Couldn't save. Retrying — your work is kept.</Chip>;
  if (state.status === 'saving') return <span className={styles.quiet}>Saving…</span>;
  return <span className={styles.quiet}>{state.savedAt ? `Saved ${formatTime(state.savedAt)}` : 'Nothing written yet'}</span>;
}

/** F11.06: both this device and another changed it. Keeping this device's is the default. */
function ConflictBanner({ state }: { readonly state: ScorecardDraftState }): ReactNode {
  return (
    <div className={styles.conflict} role="alertdialog" aria-label="Scorecard changed on another device">
      <p className={styles.conflictText}>
        This scorecard was also changed on another device{state.conflict?.savedAt ? ` at ${formatTime(state.conflict.savedAt)}` : ''}. Which version do you want to keep?
      </p>
      <div className={styles.conflictActions}>
        <Button variant="solid" onClick={state.keepMine} autoFocus>
          Keep this device's version
        </Button>
        <Button variant="outline" onClick={state.useTheirs}>
          Use the other version
        </Button>
      </div>
    </div>
  );
}

function StateBanner({ view, isEditing, onEdit }: { readonly view: CockpitView; readonly isEditing: boolean; readonly onEdit: () => void }): ReactNode {
  const card = view.scorecard;
  if (card.status === 'waived') return <p className={styles.notice}>HR waived this scorecard: "{card.waiverReason}". It no longer counts for this round.</p>;
  if (card.currentVersion === 0) {
    return card.dueAt ? <p className={styles.notice}>Due {formatDateTime(card.dueAt)}.</p> : null;
  }
  return (
    <p className={styles.submittedBanner}>
      <Lock size={14} aria-hidden /> Submitted{card.submittedAt ? ` ${formatDateTime(card.submittedAt)}` : ''} · version {card.currentVersion}.
      {view.can.write && !isEditing && (
        <>
          {' '}
          <button type="button" className={styles.inlineLink} onClick={onEdit}>
            Edit it
          </button>{' '}
          — your change becomes version {card.currentVersion + 1}, and version {card.currentVersion} is kept.
        </>
      )}
    </p>
  );
}

/** F11.10 — late arrival, cut short, technical failure. Each can be taken back. */
function FlagMenu({ interviewId, flags }: { readonly interviewId: string; readonly flags: readonly InterviewFlagView[] }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle: togglePopover, close } = usePopover();
  const queryClient = useQueryClient();
  const toasts = useToasts();
  const raised = new Set(flags.map((flag) => flag.kind));

  async function toggle(kind: InterviewFlagKind, isOn: boolean): Promise<void> {
    const key = cockpitKey(interviewId);
    const before = queryClient.getQueryData<CockpitView>(key);
    // §5a rule 1: the tick shows at once; the server confirms after.
    queryClient.setQueryData<CockpitView>(key, (current) =>
      current ? { ...current, flags: isOn ? [...current.flags, { kind, note: null, byName: null, at: new Date().toISOString() }] : current.flags.filter((flag) => flag.kind !== kind) } : current,
    );
    try {
      const next = isOn ? await scorecardsApi.flag(interviewId, { kind, note: null }) : await scorecardsApi.unflag(interviewId, kind);
      queryClient.setQueryData<CockpitView>(key, (current) => (current ? { ...current, flags: next } : current));
    } catch (error) {
      queryClient.setQueryData(key, before);
      toasts.show({ tone: 'danger', message: "Couldn't change the flag", detail: errorMessage(error) });
    }
  }

  return (
    <>
      <Button ref={triggerRef} variant={flags.length > 0 ? 'quiet' : 'ghost'} icon={Flag} onClick={togglePopover} aria-expanded={isOpen}>
        {flags.length > 0 ? `Flagged (${flags.length})` : 'Flag an issue'}
      </Button>
      <Popover anchor={anchor} onClose={close} label="Flag an issue" align="right">
        <div className={styles.flagMenu}>
          <p className={styles.quiet}>Recorded on the interview for the decision.</p>
          {INTERVIEW_FLAG_KINDS.map((kind) => (
            <Checkbox key={kind} checked={raised.has(kind)} label={INTERVIEW_FLAG_LABELS[kind]} onChange={(checked) => void toggle(kind, checked)} />
          ))}
        </div>
      </Popover>
    </>
  );
}

/** 06-qol §2: the cockpit has its own keys, so it shows them — once, until dismissed. */
function HintBar({ userId, hasKits }: { readonly userId: string; readonly hasKits: boolean }): ReactNode {
  const store = useMemo(() => createUserPreferenceStore(userId), [userId]);
  const [isDismissed, setIsDismissed] = useState(() => store.get(HINTS_KEY, false));
  if (isDismissed) return null;
  return (
    <div className={styles.hints} role="note" aria-label="Keyboard shortcuts">
      {hasKits && (
        <span>
          <Kbd>K</Kbd> interview kit
        </span>
      )}
      <span>
        <Kbd>←</Kbd>
        <Kbd>→</Kbd> move along a score row
      </span>
      <span>
        <Kbd>{MOD_KEY}</Kbd>
        <Kbd>Enter</Kbd> submit
      </span>
      {hasKits && (
        <span>
          <Kbd>Esc</Kbd> close the kit
        </span>
      )}
      <Button
        size="sm"
        variant="ghost"
        icon={X}
        aria-label="Hide these hints"
        onClick={() => {
          store.set(HINTS_KEY, true);
          setIsDismissed(true);
        }}
      />
    </div>
  );
}

function clock(ms: number): string {
  const totalSeconds = Math.floor(ms / SECOND_MS);
  const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}
